import React from "react";
import Card from "components/card";
import {
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { MdSearch, MdErrorOutline, MdInbox, MdDownload, MdExpandMore, MdUnfoldMore, MdUnfoldLess } from "react-icons/md";
import { downloadCsv, rowsToCsv } from "./tableExport";
import { ColumnVisibilityMenu, TableFilter, PageSizeSelect } from "./TableControls";

const HIDE_BELOW = {
  sm: "hidden sm:table-cell",
  md: "hidden md:table-cell",
  lg: "hidden lg:table-cell",
  xl: "hidden xl:table-cell",
};

const SPAN = {
  1: "",
  2: "sm:col-span-2",
  3: "sm:col-span-2 lg:col-span-3",
};

const GRID_COLUMNS = {
  1: "grid-cols-1",
  2: "grid-cols-1 sm:grid-cols-2",
  3: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3",
  4: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4",
};

export function DetailGrid({ children, columns = 3, className = "" }) {
  return <dl className={`grid gap-x-6 gap-y-3 ${GRID_COLUMNS[columns] || GRID_COLUMNS[3]} ${className}`}>{children}</dl>;
}

export function DetailItem({ label, children, span = 1, mono = false, emphasis = false }) {
  const empty = children === null || children === undefined || children === "";
  return (
    <div className={`min-w-0 ${SPAN[span] || ""}`}>
      <dt className="text-xs font-medium text-muted-foreground">{label}</dt>
      <dd
        className={`mt-0.5 break-words text-navy-700 dark:text-white ${mono ? "font-mono text-xs" : "text-sm"} ${emphasis ? "font-semibold" : ""}`}
      >
        {empty ? <span className="text-gray-400">—</span> : children}
      </dd>
    </div>
  );
}

export function DetailSection({ title, children, className = "" }) {
  return (
    <div className={className}>
      {title && <p className="mb-2 text-xs font-medium text-muted-foreground">{title}</p>}
      {children}
    </div>
  );
}

function defaultRowId(row, index) {
  return String(row?.id ?? row?.userId ?? index);
}

function isInteractive(target) {
  return !!(target && typeof target.closest === "function" && target.closest("button, a, input, select, textarea, label, [data-no-expand]"));
}

const iconButton =
  "flex h-7 w-7 items-center justify-center rounded-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring/50";

export default function ExpandableDataTable({
  title,
  subtitle,
  columns,
  data,
  loading,
  error,
  onRetry,
  emptyMessage = "Nothing to show yet.",
  search,
  onSearchChange,
  searchPlaceholder = "Search…",
  page = 1,
  pageSize = 20,
  total = 0,
  onPageChange,
  headerActions,
  notice,
  exportFilename,
  onExportAll,
  exportColumns,
  renderDetail,
  // The superadmin portal's copy of this component names the same prop
  // `renderExpanded`, and passing the wrong one used to render an empty panel with no
  // error at all — that is exactly how five views here shipped silently blank. Accepting
  // both means a mismatch degrades to "still works" instead of "silently blank".
  renderExpanded,
  renderActions,
  onPageSizeChange,
  getRowId = defaultRowId,
  rowLabel = (row) => row?.id ?? "",
  actionsLabel = "Actions",
  serverSort,
  onServerSortChange,
  filterBar,
}) {
  const resolveDetail = renderDetail || (renderExpanded ? (original, row) => renderExpanded(row) : undefined);
  const [sorting, setSorting] = React.useState([]);
  const [expanded, setExpanded] = React.useState({});
  const [columnVisibility, setColumnVisibility] = React.useState({});
  const [globalFilter, setGlobalFilter] = React.useState("");
  const [exporting, setExporting] = React.useState(false);
  const [exportError, setExportError] = React.useState(null);
  const [overflows, setOverflows] = React.useState(false);
  const scrollRef = React.useRef(null);
  const instanceId = React.useId();

  React.useEffect(() => {
    setExpanded({});
  }, [page, search]);

  const allColumns = React.useMemo(
    () => [
      {
        id: "__expander",
        enableSorting: false,
        header: ({ table }) => {
          const allOpen = table.getIsAllRowsExpanded();
          return (
            <button
              type="button"
              onClick={table.getToggleAllRowsExpandedHandler()}
              aria-label={allOpen ? "Collapse all rows" : "Expand all rows"}
              title={allOpen ? "Collapse all" : "Expand all"}
              className={iconButton}
            >
              {allOpen ? <MdUnfoldLess className="text-lg" /> : <MdUnfoldMore className="text-lg" />}
            </button>
          );
        },
        cell: ({ row }) => {
          const open = row.getIsExpanded();
          return (
            <button
              type="button"
              onClick={row.getToggleExpandedHandler()}
              aria-expanded={open}
              aria-controls={`${instanceId}-panel-${row.id}`}
              aria-label={`${open ? "Hide" : "Show"} details for ${rowLabel(row.original) || `row ${row.index + 1}`}`}
              className={iconButton}
            >
              <MdExpandMore className={`text-xl transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
            </button>
          );
        },
        meta: { width: "w-9" },
      },
      ...columns,
    ],
    [columns, instanceId, rowLabel],
  );

  const table = useReactTable({
    data: data || [],
    columns: allColumns,
    state: { sorting, expanded, columnVisibility, globalFilter },
    onSortingChange: setSorting,
    onExpandedChange: setExpanded,
    onColumnVisibilityChange: setColumnVisibility,
    onGlobalFilterChange: setGlobalFilter,
    getRowCanExpand: () => true,
    getRowId,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
  });

  const rows = table.getRowModel().rows;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const hasRows = (data || []).length > 0;

  React.useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    const measure = () => setOverflows(el.scrollWidth > el.clientWidth + 1);
    measure();
    let observer;
    try {
      observer = new ResizeObserver(measure);
      observer.observe(el);
    } catch {
      window.addEventListener("resize", measure);
    }
    return () => {
      if (observer) observer.disconnect();
      else window.removeEventListener("resize", measure);
    };
  }, [hasRows, loading, error, expanded]);

  async function handleExport() {
    setExporting(true);
    setExportError(null);
    try {
      const all = onExportAll ? await onExportAll() : data || [];
      downloadCsv(exportFilename || "export", rowsToCsv(exportColumns || columns, all));
    } catch (err) {
      setExportError(err.message || "Could not export this table.");
    } finally {
      setExporting(false);
    }
  }

  function handleRowClick(row, event) {
    if (isInteractive(event.target)) return;
    if (window.getSelection && String(window.getSelection()).length > 0) return;
    row.toggleExpanded();
  }

  return (
    <Card extra="w-full h-full px-4 pb-6 sm:px-6">
      <div className="flex flex-col gap-3 pt-5 pb-1 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold text-foreground">{title}</h2>
          {subtitle && <p className="mt-0.5 text-sm text-muted-foreground">{subtitle}</p>}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {onSearchChange && (
            <div className="relative min-w-[180px] flex-1 sm:w-56 sm:flex-none">
              <MdSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base text-muted-foreground" />
              <input
                type="search"
                placeholder={searchPlaceholder}
                value={search}
                onChange={(e) => onSearchChange(e.target.value)}
                aria-label={searchPlaceholder}
                className="h-9 w-full rounded-sm border border-border bg-background pl-9 pr-3 text-sm text-foreground outline-none transition-[color,box-shadow] placeholder:text-muted-foreground focus:border-ring focus:ring-[3px] focus:ring-ring/50"
              />
            </div>
          )}

          {exportFilename && (
            <button
              type="button"
              onClick={handleExport}
              disabled={exporting || !hasRows}
              className="flex h-9 items-center gap-1.5 whitespace-nowrap rounded-sm border border-border bg-background px-3 text-sm font-medium text-foreground transition-colors hover:bg-accent disabled:opacity-40"
            >
              <MdDownload className="text-sm" />
              {exporting ? "Exporting…" : "Export CSV"}
            </button>
          )}

          <ColumnVisibilityMenu table={table} />
          {hasRows && <TableFilter table={table} rowCount={(data || []).length} />}

          {headerActions}
        </div>
      </div>

      {exportError && <p className="mt-1 text-xs font-medium text-red-500">{exportError}</p>}
      {filterBar && <div className="mt-3">{filterBar}</div>}
      {notice && <div className="mt-3">{notice}</div>}

      {overflows && !error && !loading && hasRows && (
        <p className="mt-2 text-xs text-muted-foreground">Swipe sideways to see every column →</p>
      )}

      <div className="relative mt-3">
        <div ref={scrollRef} className="overflow-x-auto">
          {error ? (
            <div className="flex flex-col items-center gap-2 py-16 text-center">
              <MdErrorOutline className="text-3xl text-muted-foreground" />
              <p className="text-sm font-medium text-destructive">{error}</p>
              {onRetry && (
                <button
                  type="button"
                  onClick={onRetry}
                  className="mt-2 inline-flex h-8 items-center rounded-sm border border-border bg-background px-3 text-sm font-medium text-foreground transition-colors hover:bg-accent"
                >
                  Retry
                </button>
              )}
            </div>
          ) : loading ? (
            <div className="flex flex-col gap-3 py-8" aria-busy="true" aria-label="Loading">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="h-10 w-full animate-pulse rounded-md bg-accent" />
              ))}
            </div>
          ) : !hasRows ? (
            <div className="flex flex-col items-center gap-2 py-16 text-center">
              <MdInbox className="text-3xl text-muted-foreground/60" />
              <p className="text-sm font-medium text-muted-foreground">{emptyMessage}</p>
            </div>
          ) : (
            <table className="w-full table-auto border-collapse">
              <thead>
                {table.getHeaderGroups().map((hg) => (
                  <tr key={hg.id}>
                    {hg.headers.map((header) => {
                      const meta = header.column.columnDef.meta || {};
                      const serverMode = !!onServerSortChange;
                      const canSort = serverMode ? !!meta.sortKey : header.column.getCanSort();
                      const sorted = serverMode ? (serverSort && meta.sortKey && serverSort.sort === meta.sortKey ? serverSort.dir : false) : header.column.getIsSorted();
                      const toggleSort = serverMode
                        ? () => onServerSortChange(meta.sortKey, sorted ? (sorted === "asc" ? "desc" : "asc") : meta.sortFirst || "desc")
                        : header.column.getToggleSortingHandler();
                      return (
                        <th
                          key={header.id}
                          scope="col"
                          aria-sort={sorted === "asc" ? "ascending" : sorted === "desc" ? "descending" : undefined}
                          className={`h-10 border-b border-border pr-4 text-start align-middle last:pr-0 ${HIDE_BELOW[meta.hideBelow] || ""} ${meta.width || ""} ${meta.align === "right" ? "text-right" : ""}`}
                        >
                          {header.isPlaceholder ? null : canSort ? (
                            <button
                              type="button"
                              onClick={toggleSort}
                              title={serverMode ? "Sort all results by this column" : undefined}
                              className={`flex items-center gap-1 whitespace-nowrap text-xs font-medium text-muted-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 ${meta.align === "right" ? "ml-auto" : ""}`}
                            >
                              {flexRender(header.column.columnDef.header, header.getContext())}
                              <span className="text-muted-foreground/60" aria-hidden="true">
                                {{ asc: "▲", desc: "▼" }[sorted] ?? ""}
                              </span>
                            </button>
                          ) : (
                            <div className="flex items-center whitespace-nowrap text-xs font-medium text-muted-foreground">
                              {flexRender(header.column.columnDef.header, header.getContext())}
                            </div>
                          )}
                        </th>
                      );
                    })}
                  </tr>
                ))}
              </thead>
              <tbody>
                {rows.map((row) => {
                  const open = row.getIsExpanded();
                  const panelId = `${instanceId}-panel-${row.id}`;
                  const visibleCells = row.getVisibleCells();
                  const actions = renderActions ? renderActions(row.original, row) : null;
                  return (
                    <React.Fragment key={row.id}>
                      <tr
                        onClick={(e) => handleRowClick(row, e)}
                        className={`cursor-pointer border-b transition-colors ${
                          open
                            ? "border-transparent bg-muted/60"
                            : "border-border hover:bg-muted/50"
                        }`}
                      >
                        {visibleCells.map((cell) => {
                          const meta = cell.column.columnDef.meta || {};
                          return (
                            <td
                              key={cell.id}
                              className={`h-12 py-2 pr-4 align-middle text-sm last:pr-0 ${meta.wrap ? "" : "whitespace-nowrap"} ${HIDE_BELOW[meta.hideBelow] || ""} ${meta.width || ""} ${meta.align === "right" ? "text-right" : ""} ${meta.className || ""}`}
                            >
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </td>
                          );
                        })}
                      </tr>
                      {open && (
                        <tr className="border-b border-border">
                          <td colSpan={visibleCells.length} className="p-0">
                            <div
                              id={panelId}
                              role="region"
                              aria-label={`Details for ${rowLabel(row.original) || `row ${row.index + 1}`}`}
                              className="mx-1 mb-3 rounded-lg border border-border bg-background p-4"
                            >
                              {resolveDetail ? resolveDetail(row.original, row) : null}
                              {actions && (
                                <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-border pt-3">
                                  <span className="mr-1 text-xs font-medium text-muted-foreground">{actionsLabel}</span>
                                  {actions}
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {onPageChange && !loading && !error && total > 0 && (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 pt-2">
          <div className="flex flex-wrap items-center gap-3">
            <p className="text-sm text-muted-foreground">
              Page {page} of {totalPages} &middot; {total.toLocaleString()} total
              {rows.length !== (data || []).length && ` · ${rows.length} shown after filter`}
            </p>
            <PageSizeSelect pageSize={pageSize} onPageSizeChange={onPageSizeChange} />
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => onPageChange(page - 1)}
              className="inline-flex h-8 items-center rounded-sm border border-border bg-background px-3 text-sm font-medium text-foreground transition-colors hover:bg-accent disabled:opacity-40"
            >
              ← Previous
            </button>
            <button
              type="button"
              disabled={page >= totalPages}
              onClick={() => onPageChange(page + 1)}
              className="inline-flex h-8 items-center rounded-sm border border-border bg-background px-3 text-sm font-medium text-foreground transition-colors hover:bg-accent disabled:opacity-40"
            >
              Next →
            </button>
          </div>
        </div>
      )}
    </Card>
  );
}
